import React, { useEffect, useState, useCallback } from 'react'
import { css } from '@emotion/core'
import { usePageView } from '@fs/zion-analytics'
import {
  Grid,
  Cell,
  Checkbox,
  ResponsiveTableRow,
  TextField,
  Button,
  Row,
  colors,
  useContainerWidth,
  H5,
} from '@fs/zion-ui'
import { FamilyCard } from '@fs/zion-tree-person-info-cards'
import { OrdinanceStatus } from '@fs/zion-ordinances-status'
import { useTranslation } from 'react-i18next'
import _ from 'lodash'
import { DmcResponsiveTable } from '../common/DmcResponsiveTable'
import { usePageTitle } from '../app/PageTitleContext'
import { useQueryParams } from '../../util/UrlUtil'
import PersonInfo from '../person/relationships/PersonInfo'
import useNotifications from '../../hooks/useNotifications'
import { getOrdinanceStatusForPerson } from '../../services/ordinanceService'
import { getUser, getUserWithUsername } from '../../services/userService'
import { getPersonSummary } from '../../services/personService'
import { capitalizeFirstLetter, enumToCamelCase, getLastPathElement } from '../../util/StringUtil'
import { UserLink, PersonLink, GroupLink } from '../app/Links'
import { createReservations } from '../../services/reservationsService'
import TempleLabel from '../common/TempleLabel'
import useTempleDirectory from '../../hooks/useTempleDirectory'
import { convertUTCString, utcToLocaleString } from '../../util/DateUtil'
import { isWKC } from '../../util/EntityUtil'
import UserPicker from '../common/UserPicker'
import { mapParentRelationshipToFamily, mapSpouseRelationshipToFamily } from '../relationship/relationshipUtils'

const labelStyle = css`
  color: grey;
  font-size: 0.75rem;
  padding-top: 0;
  padding-inline-end: 0.75em;
  padding-bottom: 0;
  padding-inline-start: 0;
`
const detailStyle = css`
  font-size: 0.9rem;
  white-space: pre-wrap;
`

const smallLink = css`
  margin-inline-start: 5px !important;
  margin-bottom: 2px !important;
  font-size: smaller;
`

const errorMessageStyle = css`
  color: ${colors.red60};
  font-size: 1em;
`

const spKey = 'sealingToParents'
const ssKey = 'sealingToSpouse'

// Move these helper functions outside the component or create a separate utils file
const shorthandLookup = {
  BAPTISM: 'B',
  CONFIRMATION: 'C',
  INITIATORY: 'I',
  ENDOWMENT: 'E',
  SEALING_TO_SPOUSE: 'SS',
  SEALING_TO_PARENTS: 'SP',
}

const getCheckedStatus = (ordinance, key) => {
  return ordinance.status === 'READY' && ['baptism', 'confirmation', 'endowment', 'initiatory'].includes(key)
}

const buildStatusReasonText = (ordinance, t) => {
  let statusReasonTxt = ''
  if (ordinance.statusReasons) {
    statusReasonTxt = `${ordinance.statusReasons
      .map((statusReason) =>
        t(`ordinanceStatusReason.${statusReason}`, capitalizeFirstLetter(statusReason.toLowerCase()))
      )
      .join(' ')} `
  }
  return statusReasonTxt
}

const apiStatusToZionStatus = {
  RESERVED: 'InProgressNotPrinted',
}

const createOrdinanceStatus = (ordinance, key, t) => {
  if (!ordinance) return null

  const statusReasonTxt = buildStatusReasonText(ordinance, t)
  const statusForZion = apiStatusToZionStatus[ordinance.status] ?? enumToCamelCase(ordinance.status)
  const statusText = t(`ordinances.status.${ordinance.status}`, enumToCamelCase(ordinance.status))

  return {
    ordinanceType: ordinance.ordinanceType,
    status: statusForZion,
    statusText,
    shorthand: shorthandLookup[ordinance.ordinanceType],
    statusReasonTxt,
    templeDetails: ordinance.templeDetails,
    reservationDetails: ordinance.reservationDetails,
    hoverText: ordinance.ordinanceType,
    parent1: ordinance.parent1?.personId,
    parent2: ordinance.parent2?.personId,
    spouse: ordinance.spouse?.personId,
    checked: ordinance.checked || getCheckedStatus(ordinance, key),
    disabled: ordinance.status !== 'READY',
  }
}

const setFirstReadyParentsChecked = (ordinances, parent1IdParam, parent2IdParam) => {
  let firstReadyOrdinance = null

  if (parent1IdParam && parent2IdParam) {
    firstReadyOrdinance = ordinances.find(
      (ord) =>
        ord.parent1?.personId === parent1IdParam &&
        ord.parent2?.personId === parent2IdParam &&
        ord.ordinanceType === 'SEALING_TO_PARENTS' &&
        ord.status === 'READY'
    )
  } else {
    firstReadyOrdinance = ordinances.find((ord) => ord.ordinanceType === 'SEALING_TO_PARENTS' && ord.status === 'READY')
  }

  if (firstReadyOrdinance) {
    firstReadyOrdinance.checked = true
  }
}

const setFirstReadySpouseChecked = (ordinances, spouseIdParam) => {
  let firstReadyOrdinance = null

  if (spouseIdParam) {
    firstReadyOrdinance = ordinances.find(
      (ord) =>
        ord.spouse?.personId === spouseIdParam && ord.ordinanceType === 'SEALING_TO_SPOUSE' && ord.status === 'READY'
    )
  } else {
    firstReadyOrdinance = ordinances.find((ord) => ord.ordinanceType === 'SEALING_TO_SPOUSE' && ord.status === 'READY')
  }

  if (firstReadyOrdinance) {
    firstReadyOrdinance.checked = true
  }
}

const processSpouseOrdinance = (ord, newOrdinanceStatuses, key, spouseIdParam, t) => {
  if (spouseIdParam) {
    if (ord.spouse.personId === spouseIdParam) {
      newOrdinanceStatuses[key] = createOrdinanceStatus(ord, key, t)
    }
  } else {
    newOrdinanceStatuses[key] = createOrdinanceStatus(ord, key, t)
  }
}

const processParentsOrdinance = (ord, newOrdinanceStatuses, key, parent1IdParam, parent2IdParam, t) => {
  if (parent1IdParam && parent2IdParam) {
    if (
      (ord.parent1?.personId === parent1IdParam && ord.parent2?.personId === parent2IdParam) ||
      (ord.parent1?.personId === parent2IdParam && ord.parent2?.personId === parent1IdParam)
    ) {
      newOrdinanceStatuses[key] = createOrdinanceStatus(ord, key, t)
    }
  } else {
    newOrdinanceStatuses[key] = createOrdinanceStatus(ord, key, t)
  }
}

const processOrdinanceArray = (
  ordinances,
  keyName,
  newOrdinanceStatuses,
  parent1IdParam,
  parent2IdParam,
  spouseIdParam,
  t
) => {
  setFirstReadyParentsChecked(ordinances, parent1IdParam, parent2IdParam)
  setFirstReadySpouseChecked(ordinances, spouseIdParam)

  ordinances.forEach((ord, i) => {
    const key = `${keyName}${i}`

    if (ord.ordinanceType === 'SEALING_TO_SPOUSE') {
      processSpouseOrdinance(ord, newOrdinanceStatuses, key, spouseIdParam, t)
    }

    if (ord.ordinanceType === 'SEALING_TO_PARENTS') {
      processParentsOrdinance(ord, newOrdinanceStatuses, key, parent1IdParam, parent2IdParam, t)
    }
  })
}

export default function Ordinance110YearApproval() {
  usePageView({ page_detail: 'DMConsole:Reservation110YearApproval' })
  const { t } = useTranslation()
  usePageTitle(t('pageTitle.tools.reservation110YearApproval'))
  const { error, ok, showServerNotifications } = useNotifications()
  const queryParams = useQueryParams()
  const [userNameParam, setUserNameParam] = useState(queryParams?.get('userName') || queryParams?.get('username'))
  const [personIdParam, setPersonIdParam] = useState(queryParams?.get('personId'))
  const [userIdParam, setUserIdParam] = useState(
    queryParams?.get('userId') || queryParams?.get('proxyUserId') || queryParams?.get('proxyCPUserId')
  )
  const [spouseIdParam, setSpouseIdParam] = useState(queryParams?.get('spouseId'))
  const [parent1IdParam, setParent1IdParam] = useState(queryParams?.get('parent1Id'))
  const [parent2IdParam, setParent2IdParam] = useState(queryParams?.get('parent2Id'))

  const [user, setUser] = useState(null)
  const [person, setPerson] = useState(null)
  const [ordinanceStatuses, setOrdinanceStatuses] = useState()
  const [nonMemberError, setNonMemberError] = useState()
  const [parents, setParents] = useState([])
  const [spouses, setSpouses] = useState([])
  const [ownerData, setOwnerData] = useState({})

  const MAX_RETRIES = 4
  const MAX_DELAY = 10000

  useEffect(() => {
    if (!user && userNameParam) {
      setNonMemberError(false)
      getUserWithUsername(userNameParam)
        .then((results) => {
          if (results.data[0]) {
            setUser(results.data[0])
            if (results.data[0].identity.users[0].accountType === 'NONMEMBER') {
              setNonMemberError(true)
            }
          }
        })
        .catch((err) => {
          error(
            t('reservation.errors.findingUserWithName', {
              defaultValue: `Error finding user with name ${userNameParam}`,
              name: userNameParam,
            }),
            err
          )
        })
    }
  }, [error, user, userNameParam, t])

  useEffect(() => {
    if (!user && userIdParam) {
      getUser(userIdParam, userIdParam.startsWith('cis.user.') ? 'cisId' : 'contributorId')
        .then((results) => {
          setUser(results.data)
          setOwnerData((prevState) => {
            return { [results.data.contributorId]: results.data, ...prevState }
          })
          if (results.data.identity.users[0].accountType === 'NONMEMBER') {
            setNonMemberError(true)
          }
        })
        .catch((err) => {
          error(
            t('user.errors.gettingUser', {
              defaultValue: `Error getting user: ${err.message || ''}`,
              message: err.message,
            }),
            err
          )
        })
    }
  }, [error, user, userIdParam, t])

  useEffect(() => {
    if (!person && personIdParam) {
      getPersonSummary(personIdParam)
        .then((personResults) => {
          setPerson(personResults)
          personResults?.relationships?.parents?.forEach((relationship) => {
            if (
              (relationship.parent1.personId === parent1IdParam && relationship.parent2.personId === parent2IdParam) ||
              (relationship.parent1.personId === parent2IdParam && relationship.parent2.personId === parent1IdParam)
            ) {
              // get siblings
              getPersonSummary(parent1IdParam).then((fatherResults) => {
                relationship.children = fatherResults?.relationships?.children
              })
            }
          })
        })
        .catch((err) => {
          error(
            t('reservation.errors.findingPerson', {
              defaultValue: `Error finding person ${personIdParam}`,
              personId: personIdParam,
            }),
            err
          )
        })
    }
  }, [error, parent1IdParam, parent2IdParam, person, personIdParam, spouseIdParam, t])

  useEffect(() => {
    const fetchOwnerUserData = async () => {
      const ownerIds = ordinanceStatuses && [
        ...new Set(
          Object.values(ordinanceStatuses)
            .map((ordinanceStatus) => ordinanceStatus?.reservationDetails?.ownerId)
            .filter((ownerId) => ownerId && (ownerData ? !Object.keys(ownerData).includes(ownerId) : true))
        ),
      ]
      let ownerUserData
      ownerIds &&
        ownerIds.length > 0 &&
        (await Promise.allSettled(
          ownerIds.map((ownerId) => {
            return getUser(getLastPathElement(ownerId), 'contributorId').then((reservationUserResult) => {
              return { [ownerId]: reservationUserResult.data }
            })
          })
        ).then((results) => {
          ownerUserData = results.map((result) => result.value).reduce((prevObj, obj) => Object.assign(prevObj, obj))
        }))
      ownerUserData &&
        setOwnerData((prevState) => {
          return Object.assign(ownerUserData, prevState)
        })
    }
    fetchOwnerUserData()
  }, [ordinanceStatuses, ownerData])

  const fetchOrdinanceData = useCallback(
    async (personId) => {
      const response = await getOrdinanceStatusForPerson(personId)

      if (!(response && response.status === 200)) {
        showServerNotifications(response?.data?.notifications, 'error')
        return null
      }

      return response.data
    },
    [showServerNotifications]
  )

  const getOrdinanceStatuses = useCallback(
    async (personId) => {
      try {
        let ordinanceData = await fetchOrdinanceData(personId)
        if (ordinanceData.responseStatus === 'CALCULATING') {
          const retryWithBackoff = async (attempt = 0) => {
            const data = await fetchOrdinanceData(personId)
            if (data.responseStatus === 'CALCULATING' && attempt < MAX_RETRIES) {
              const delay = Math.min(1000 * 2 ** attempt, MAX_DELAY) // exponential backoff, max 10s
              await new Promise((resolve) => {
                setTimeout(resolve, delay)
              })
              return retryWithBackoff(attempt + 1)
            }
            return data
          }
          ordinanceData = await retryWithBackoff()

          if (ordinanceData.responseStatus !== 'SUCCESS') {
            showServerNotifications(
              [
                {
                  message: t('ordinances.tryLater', 'Ordinance status is being updated. Please wait...'),
                },
              ],
              'error'
            )
            ordinanceData = null
          }
        }

        if (!ordinanceData) return

        const newOrdinanceStatuses = {}

        Object.entries(ordinanceData).forEach(([key, ordinance]) => {
          if (ordinance && Array.isArray(ordinance)) {
            processOrdinanceArray(
              ordinance,
              key,
              newOrdinanceStatuses,
              parent1IdParam,
              parent2IdParam,
              spouseIdParam,
              t
            )
          } else if (['B', 'C', 'I', 'E'].includes(ordinance?.ordinanceType?.charAt(0))) {
            // Handle individual ordinances (BCIE)
            newOrdinanceStatuses[key] = createOrdinanceStatus(ordinance, key, t)
          }
        })

        setOrdinanceStatuses(newOrdinanceStatuses)
      } catch (err) {
        showServerNotifications(err?.response?.data?.notifications, 'error')
      }
    },
    [fetchOrdinanceData, parent1IdParam, parent2IdParam, showServerNotifications, spouseIdParam, t]
  )

  useEffect(() => {
    if (person) {
      if (person.id) {
        getOrdinanceStatuses(person.id)
      }
    }
    return () => {
      setOrdinanceStatuses()
    }
  }, [getOrdinanceStatuses, parent1IdParam, parent2IdParam, person, showServerNotifications, spouseIdParam, t])

  const setSpouseGenderOrder = (personIds, ordinanceStatus) => {
    personIds.spouse1Id = person.gender === 'MALE' ? person.id : ordinanceStatus.spouse
    personIds.spouse2Id = person.gender === 'MALE' ? ordinanceStatus.spouse : person.id
  }

  const reserve = () => {
    const ordinanceTypes = []
    const personIds = {
      individualId: person.id,
    }
    ordinanceStatuses &&
      Object.entries(ordinanceStatuses).forEach((entry) => {
        const [key, ordinanceStatus] = entry
        if (ordinanceStatus.checked) {
          ordinanceTypes.push(ordinanceStatus.ordinanceType)
          if (key.includes(spKey)) {
            personIds.childId = person.id
            personIds.parent1Id = ordinanceStatus.parent1
            personIds.parent2Id = ordinanceStatus.parent2
          }
          if (key.includes(ssKey)) {
            setSpouseGenderOrder(personIds, ordinanceStatus)
          }
        }
      })

    const request = {
      ownerId: user.contributorId,
      ordinanceTypes,
      personIds,
    }

    createReservations(request)
      .then(() => {
        getOrdinanceStatuses(person.id)
        ok(t('reservation.createdReservations', 'Created reservations'))
      })
      .catch((err) => {
        error(
          t('reservation.errors.creatingReservations', {
            defaultValue: `Error creating reservations: ${err.message || ''}`,
            message: err.message,
          }),
          err
        )
      })
  }

  const reset = () => {
    setUserNameParam()
    setUserIdParam()
    setUser()
    setPersonIdParam()
    setPerson()
    setSpouseIdParam()
    setParent1IdParam()
    setParent2IdParam()
  }

  if ((personIdParam && (userNameParam || userIdParam)) || (person && user)) {
    return (
      <>
        {nonMemberError && (
          <div css={errorMessageStyle}>
            {`${t('user.error.nonMember', 'User is non-member')}: ${userNameParam} (${user?.contributorId}) `}
          </div>
        )}
        {!nonMemberError && (
          <>
            <UserSection user={user} />
            <AncestorSection
              person={person}
              parents={parents}
              setParents={setParents}
              spouses={spouses}
              setSpouses={setSpouses}
              spouseIdParam={spouseIdParam}
            />
            <OrdinancesSection
              ordinanceStatuses={ordinanceStatuses}
              setOrdinanceStatuses={setOrdinanceStatuses}
              parents={parents}
              spouses={spouses}
              ownerData={ownerData}
            />
          </>
        )}

        <ButtonSection
          valid={
            user &&
            person &&
            ordinanceStatuses &&
            Object.values(ordinanceStatuses)?.find((ordinanceStatus) => ordinanceStatus.checked) &&
            !nonMemberError
          }
          onSubmit={reserve}
          reset={reset}
        />
      </>
    )
  }

  const onClick = (inputPerson, inputUser) => {
    setPerson(inputPerson)
    setUser(inputUser)
  }

  return (
    <ApprovalInputForm
      userName={userNameParam}
      personId={personIdParam}
      onClick={onClick}
      setOwnerData={setOwnerData}
    />
  )
}

const UserSection = ({ user }) => {
  const { t } = useTranslation()
  const userIdentity = user?.identity?.users[0]
  return (
    <>
      <div>
        <H5 supplementary>{t('requestedBy', 'Requested by')}</H5>
      </div>
      {user && userIdentity && (
        <Grid gutters="pico">
          <Cell columns="2">
            <span css={labelStyle}>{t('user.label', 'User')}</span>
          </Cell>
          <Cell columns="10">
            <UserLink userId={user.contributorId} userName={userIdentity.username} showId />
          </Cell>
          <Cell columns="2">
            <span css={labelStyle}>{t('usersName', "User's Name")}</span>
          </Cell>
          <Cell columns="10">
            <span css={detailStyle}>
              {userIdentity.givenName} {userIdentity.familyName}
            </span>
          </Cell>
          <Cell columns="2">
            <span css={labelStyle}>{t('contactName', 'Contact Name')}</span>
          </Cell>
          <Cell columns="10">
            <span css={detailStyle}>{userIdentity.contactName}</span>
          </Cell>
          <Cell columns="2">
            <span css={labelStyle}>{t('email', 'Email Address')}</span>
          </Cell>
          <Cell columns="10">
            <span css={detailStyle}>{userIdentity.email}</span>
          </Cell>
          <Cell columns="2">
            <span css={labelStyle}>{t('phone', 'Phone Number')}</span>
          </Cell>
          <Cell columns="10">
            <span css={detailStyle}>
              {userIdentity.sms?.country} {userIdentity.sms?.number}
            </span>
          </Cell>
          <Cell columns="2">
            <span css={labelStyle}>{t('address', 'Mailing Address')}</span>
          </Cell>
          <Cell columns="10">
            <span css={detailStyle}>{user.address}</span>
          </Cell>
        </Grid>
      )}
    </>
  )
}

const AncestorSection = ({ person, parents, setParents, spouses, setSpouses, spouseIdParam }) => {
  const { t } = useTranslation()
  const atWidth = useContainerWidth()

  useEffect(() => {
    if (person?.relationships?.parents && parents?.length === 0) {
      setParents(person.relationships.parents.map((rel) => mapParentRelationshipToFamily(rel)))
    }
  }, [person, parents, setParents])

  useEffect(() => {
    if (person?.relationships?.spouses && spouses?.length === 0) {
      setSpouses(person.relationships.spouses.map((rel) => mapSpouseRelationshipToFamily(rel, spouseIdParam, person)))
    }
  }, [person, spouses, setSpouses, spouseIdParam])

  return (
    <>
      <div>
        <H5 supplementary>{t('ancestor', 'Ancestor')}</H5>
      </div>
      <Grid>
        {person && (
          <Cell
            columns={atWidth({
              lg: 2,
              sm: 12,
            })}
          >
            <PersonInfo isTreeLink person={person} />
          </Cell>
        )}

        {person && spouses?.length > 0 && (
          <Cell
            columns={atWidth({
              lg: 5,
              sm: 12,
            })}
          >
            <FamilyCard
              families={{ parents: [], spouses }}
              focusPersonId={person.id}
              focusFamily={spouses[0]}
              type="couple"
              data-testid="110-year-spouses-card"
            />
          </Cell>
        )}
        {person && parents?.length > 0 && (
          <Cell
            columns={atWidth({
              lg: 5,
              sm: 12,
            })}
          >
            <FamilyCard
              families={{ parents, spouses: [] }}
              focusPersonId={person.id}
              focusFamily={parents[0]}
              type="parents"
              data-testid="110-year-spouses-card"
            />
          </Cell>
        )}
      </Grid>
    </>
  )
}

const OrdinancesSection = ({ ordinanceStatuses, setOrdinanceStatuses, parents, spouses, ownerData }) => {
  const { t } = useTranslation()
  const { temples } = useTempleDirectory()

  return (
    <>
      <div>
        <H5 supplementary>{t('ordinances.label', 'Ordinances')}</H5>
      </div>
      {ordinanceStatuses && (
        <DmcResponsiveTable
          atSizeColumns={{
            default: {
              labels: [
                `${t('ordinances.label', 'Ordinances')}, ${t('statusReason', 'Status Reason')} & 
              ${t('additional.information', 'Additional Information')}`,
              ],
            },
            sm: {
              labels: [
                t('ordinances.label', 'Ordinances'),
                `${t('statusReason', 'Status Reason')} & ${t('additional.information', 'Additional Information')}`,
              ],
              columnWidths: [3, 9],
            },
            xl: {
              labels: [
                t('ordinances.label', 'Ordinances'),
                t('statusReason', 'Status Reason'),
                t('additional.information', 'Additional Information'),
              ],
              columnWidths: [2, 4, 6],
            },
          }}
          totalColumnCount={3}
        >
          {Object.keys(ordinanceStatuses).map((key) => {
            const ordinanceStatus = ordinanceStatuses[key]
            if (ordinanceStatus.status === 'BornInCovenant') {
              ordinanceStatus.status = 'NotApplicable'
              ordinanceStatus.statusText = t('ordinances.status.BORN_IN_THE_COVENANT', 'Born In The Covenant')
            }
            return (
              <OrdinanceStatusRow
                key={key}
                ordinanceKey={key}
                ordinanceStatus={ordinanceStatus}
                ordinanceStatuses={ordinanceStatuses}
                setOrdinanceStatuses={setOrdinanceStatuses}
                parents={parents}
                spouses={spouses}
                ownerData={ownerData}
                temples={temples}
              />
            )
          })}
        </DmcResponsiveTable>
      )}
    </>
  )
}

const SealingComponent = ({ ordinanceStatus, parents, spouses }) => {
  if (ordinanceStatus.shorthand === 'SP') {
    const parentInfo = parents.find(
      (parent) =>
        (ordinanceStatus.parent1 === parent.parent1.id && ordinanceStatus.parent2 === parent.parent2.id) ||
        (ordinanceStatus.parent1 === parent.parent2.id && ordinanceStatus.parent2 === parent.parent1.id)
    )
    if (parentInfo) {
      return (
        <>
          {parentInfo.parent1 && <PersonInfo person={parentInfo.parent1} showId />}
          {parentInfo.parent2 && <PersonInfo person={parentInfo.parent2} showId />}
        </>
      )
    }
  }
  if (ordinanceStatus.shorthand === 'SS') {
    const spouseInfo = spouses.find(
      (spouse) => ordinanceStatus.spouse === spouse.parent1.id || ordinanceStatus.spouse === spouse.parent2.id
    )
    if (spouseInfo) {
      const spouse = spouseInfo.parent1.id === ordinanceStatus.spouse ? spouseInfo.parent1 : spouseInfo.parent2
      return spouse ? <PersonInfo person={spouse} showId /> : null
    }
  }
  return null
}

const OrdinanceCompletedComponent = ({ temples, ordinanceStatus }) => {
  if (temples && ordinanceStatus.templeDetails) {
    const templeDate = convertUTCString(ordinanceStatus.templeDetails.gedcomxCompletionDate)
    const templeDetailsData = temples[ordinanceStatus.templeDetails.temple]
    return (
      <div css={detailStyle} columns={11}>
        {templeDetailsData && <TempleLabel temple={templeDetailsData} />}
        <div>{templeDate}</div>
      </div>
    )
  }
  return null
}

const ReservationComponent = ({ ordinanceStatus, ownerData, t }) => {
  if (ordinanceStatus.reservationDetails) {
    const user = ownerData?.[ordinanceStatus.reservationDetails.ownerId]
    const date = new Date(ordinanceStatus.reservationDetails.reserveDate * 1000)
    return (
      <div css={detailStyle} columns={11}>
        <div>
          {user && (
            <UserLink
              userName={
                isWKC(getLastPathElement(ordinanceStatus.reservationDetails.ownerId))
                  ? 'FamilySearch'
                  : user.identity.users[0].username
              }
              userId={ordinanceStatus.reservationDetails.ownerId}
            />
          )}
        </div>
        <div>{utcToLocaleString(date)}</div>
        {ordinanceStatus.reservationDetails.groupId && (
          <div>
            {t('groupId', 'Group ID')}: <GroupLink groupId={ordinanceStatus.reservationDetails.groupId} />
          </div>
        )}
      </div>
    )
  }
  return null
}

const OrdinanceStatusRow = ({
  ordinanceKey,
  ordinanceStatus,
  ordinanceStatuses,
  setOrdinanceStatuses,
  parents,
  spouses,
  ownerData,
  temples,
}) => {
  const { t } = useTranslation()
  if (ordinanceStatus) {
    const uncheckSealingOfSameType = (sealingType, ordinanceStatusesCopy) => {
      if (ordinanceKey.includes(sealingType) && !ordinanceStatus.checked) {
        Object.entries(ordinanceStatusesCopy).forEach((ordinance) => {
          const [key, value] = ordinance
          if (key.includes(sealingType)) {
            value.checked = false
          }
        })
      }
    }
    const handleCheckbox = () => {
      const ordinanceStatusesCopy = { ...ordinanceStatuses }
      uncheckSealingOfSameType(spKey, ordinanceStatusesCopy)
      uncheckSealingOfSameType(ssKey, ordinanceStatusesCopy)
      if (!ordinanceKey.includes(ssKey)) {
        const statusKeyList = Object.keys(ordinanceStatusesCopy).filter(
          (key) => !key.includes(ssKey) && !ordinanceStatusesCopy[key].disabled
        )
        const index = statusKeyList.indexOf(ordinanceKey)
        statusKeyList.forEach((key, i) => {
          if (ordinanceStatusesCopy[ordinanceKey].checked && i > index) {
            ordinanceStatusesCopy[key].checked = false
          } else if (!ordinanceStatusesCopy[ordinanceKey].checked && i < index && !key.includes(spKey)) {
            ordinanceStatusesCopy[key].checked = true
          }
        })
      }
      ordinanceStatusesCopy[ordinanceKey].checked = !ordinanceStatusesCopy[ordinanceKey].checked

      setOrdinanceStatuses(ordinanceStatusesCopy)
    }
    return (
      <ResponsiveTableRow
        key={ordinanceKey}
        name={`ordinance_${ordinanceKey}`}
        column1={
          <Row>
            <Checkbox
              id={ordinanceKey}
              name={ordinanceKey}
              checked={ordinanceStatus.checked}
              disabled={ordinanceStatus.disabled}
              onChange={handleCheckbox}
            />
            <OrdinanceStatus shorthand={ordinanceStatus.shorthand} stacked ordinance={ordinanceStatus} />
          </Row>
        }
        column2={<div css={detailStyle}>{ordinanceStatus.statusReasonTxt}</div>}
        column3={
          <>
            <SealingComponent ordinanceStatus={ordinanceStatus} parents={parents} spouses={spouses} />
            <OrdinanceCompletedComponent ordinanceStatus={ordinanceStatus} temples={temples} />
            <ReservationComponent ordinanceStatus={ordinanceStatus} ownerData={ownerData} t={t} />
          </>
        }
      />
    )
  }

  return null
}

const ButtonSection = ({ onSubmit, reset, valid }) => {
  const { t } = useTranslation()
  return (
    <Row>
      <Button disabled={!valid} onClick={onSubmit} emphasis="high" alignY="nextToInput" noShrinkX>
        {t('ordinances.authorize', 'Authorize Ordinances')}
      </Button>
      <Button onClick={reset} emphasis="medium" alignY="nextToInput" noShrinkX>
        {t('action.reset', 'Reset')}
      </Button>
    </Row>
  )
}

const ApprovalInputForm = ({ personId, userName, onClick, setOwnerData }) => {
  const { t } = useTranslation()
  const [formValues, setFormValues] = useState({ personId, userName })
  const [personMessage, setPersonMessage] = useState()
  const [personErrorMessage, setPersonErrorMessage] = useState()
  const [user, setUser] = useState()
  const [person, setPerson] = useState()

  const findPerson = useCallback(
    (id) => {
      setPersonErrorMessage('')
      setPersonMessage('')
      if (id) {
        getPersonSummary(id)
          .then((results) => {
            setPerson(results)
            setPersonMessage(
              <PersonLink
                personId={results.id}
                personName={results.name}
                showId
                forward={false}
                newTab
                css={smallLink}
              />
            )
          })
          .catch(() => {
            setPersonErrorMessage(<span css={smallLink}>{t('personNotFound', 'Person not found')}</span>)
          })
      }
    },
    [t]
  )

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const findPersonDebounced = useCallback(
    _.debounce((id) => {
      findPerson(id)
    }, 500),
    []
  )

  const onPersonIdChange = (e) => {
    setPersonErrorMessage('')
    setFormValues({ ...formValues, personId: e.target.value })
    findPersonDebounced(e.target.value)
  }
  const handleSubmit = () => {
    onClick(person, user)
  }

  const handleReset = () => {
    setFormValues({ userName, personId })
    setPerson()
    setUser()
  }

  useEffect(() => {
    if (!person && personId) {
      findPerson(personId)
    }
  }, [findPerson, person, personId])

  return (
    <>
      <Grid>
        <Cell columns={3}>
          <UserPicker
            onUserChanged={(foundUser) => {
              setUser(foundUser)
              setOwnerData({ [foundUser.contributorId]: foundUser })
            }}
            id="usernameInput"
            label={t('user.input.usernameOrId', 'Username or User ID')}
          />
        </Cell>
        <Cell columns={3}>
          <TextField
            id="personIdInput"
            label={t('personId', 'Person ID')}
            value={formValues.personId}
            onChange={onPersonIdChange}
            autoComplete="on"
            helpMessage={personMessage}
            placeholder={t('personId', 'Person ID')}
            validMessage={personMessage}
            errorMessage={personErrorMessage}
          />
        </Cell>
      </Grid>
      <Row>
        <Button
          id="searchResultsBtn"
          onClick={handleSubmit}
          emphasis="high"
          alignY="nextToInput"
          noShrinkX
          disabled={!user || !person}
        >
          {t('action.search', 'Search')}
        </Button>
        <Button onClick={handleReset} emphasis="medium" alignY="nextToInput" noShrinkX>
          {t('action.cancel', 'Cancel')}
        </Button>
      </Row>
    </>
  )
}
